import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Loader2, User as UserIcon } from 'lucide-react';
import { UpdateCustomerUserData, User } from '@/shared/types/userTypes';
import { getRoleName, Role } from '@/shared/types/userRoleTypes';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { useUpdateCustomerUser } from '@/domains/customers/services/account-customers';
import { useUserRoles } from '@/shared/hooks/userRoles';

interface ManageCustomerUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  user: User;
  customerId: string;
}

const ManageCustomerUserDialog: React.FC<ManageCustomerUserDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  user,
  customerId,
}) => {
  const { customerUserRoles } = useUserRoles();
  const [selectedRole, setSelectedRole] = useState<Role>(user?.role ?? customerUserRoles[0] ?? Role.VIEWER);
  const updateCustomerUser = useUpdateCustomerUser();

  useEffect(() => {
    if (isOpen && user) {
      setSelectedRole(user.role ?? customerUserRoles[0] ?? Role.VIEWER);
    }
  }, [isOpen, user, customerUserRoles]);

  const hasChanges = selectedRole !== user?.role;
  const canSubmit = Boolean(user) && hasChanges && !updateCustomerUser.isPending && customerUserRoles.some((role) => role === selectedRole);

  const handleSubmit = async () => {
    if (!canSubmit) return;

    try {
      const userData: UpdateCustomerUserData = {
        role: selectedRole,
      };

      await updateCustomerUser.mutateAsync({
        customerId,
        userId: user.userId,
        userData,
      });

      showSuccess(SUCCESS.USER.UPDATED);
      onSuccess?.();
      onClose();
    } catch (error) {
      showErrorWithApiError(error, ERROR.USER.UPDATE);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Manage user role</DialogTitle>
          <DialogDescription>
            Update the role for this user.
          </DialogDescription>
        </DialogHeader>

        {user && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 border-b pb-3">
              <UserIcon className="h-5 w-5" />
              <div className="text-left">
                <p className="text-sm font-medium">{user.name}</p>
                <p className="text-xs text-muted-foreground">{user.email}</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`role-${user.userId}`}>Role</Label>
              <Select
                value={selectedRole}
                onValueChange={(value) => setSelectedRole(value as Role)}
              >
                <SelectTrigger id={`role-${user.userId}`} className="w-full">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {customerUserRoles.map((role) => (
                    <SelectItem key={role} value={role}>
                      {getRoleName(role)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={onClose} disabled={updateCustomerUser.isPending}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="min-w-[100px]"
              >
                {updateCustomerUser.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ManageCustomerUserDialog;